import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { BusinessIndustry } from "@gigflow/shared";
import { apiRequest } from "../api";
import { useAuth, type AuthUser } from "../auth";

export function SignupPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [form, setForm] = useState({
    ownerName: "",
    ownerEmail: "",
    password: "",
    confirmPassword: "",
    businessName: "",
    industry: "CLEANING" as BusinessIndustry,
    country: "US",
    phoneNumber: "",
    acceptTerms: false,
  });

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!form.acceptTerms) {
      setError("Please accept the terms to continue.");
      return;
    }
    setPending(true);
    try {
      const result = await apiRequest<{
        token: string;
        user: AuthUser;
        business: { id: string };
      }>("/businesses/register", {
        method: "POST",
        body: JSON.stringify({ ...form, acceptTerms: true as const }),
      });
      await setSession(result.token, result.user);
      navigate("/app/onboarding");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="auth-layout">
      <form className="auth-card" onSubmit={onSubmit}>
        <p className="eyebrow">AI website creator</p>
        <h1>Create your business</h1>
        <p className="notice">
          Pick a launch industry, then describe your business in chat — AI
          drafts the site and booking experience.
        </p>
        <div className="form-grid">
          <label>
            Owner name
            <input
              required
              value={form.ownerName}
              onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
            />
          </label>
          <label>
            Owner email
            <input
              required
              type="email"
              value={form.ownerEmail}
              onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
            />
          </label>
          <div className="form-grid two">
            <label>
              Password
              <input
                required
                type="password"
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </label>
            <label>
              Confirm password
              <input
                required
                type="password"
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm({ ...form, confirmPassword: e.target.value })
                }
              />
            </label>
          </div>
          <label>
            Business name
            <input
              required
              value={form.businessName}
              onChange={(e) =>
                setForm({ ...form, businessName: e.target.value })
              }
            />
          </label>
          <div className="form-grid two">
            <label>
              Industry
              <select
                value={form.industry}
                onChange={(e) =>
                  setForm({
                    ...form,
                    industry: e.target.value as BusinessIndustry,
                  })
                }
              >
                <option value="CLEANING">Cleaning</option>
                <option value="LANDSCAPING">Landscaping</option>
              </select>
            </label>
            <label>
              Country
              <input
                required
                maxLength={2}
                value={form.country}
                onChange={(e) =>
                  setForm({ ...form, country: e.target.value.toUpperCase() })
                }
              />
            </label>
          </div>
          <label>
            Phone number
            <input
              required
              value={form.phoneNumber}
              onChange={(e) =>
                setForm({ ...form, phoneNumber: e.target.value })
              }
            />
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) =>
                setForm({ ...form, acceptTerms: e.target.checked })
              }
            />
            I accept the terms of service
          </label>
          <button className="btn btn-primary" type="submit" disabled={pending}>
            {pending ? "Creating..." : "Continue to AI setup"}
          </button>
          {error ? <p className="notice error">{error}</p> : null}
          <p className="notice">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </div>
      </form>
    </div>
  );
}
